/**
 * Dexie.js database for task persistence.
 * Shared by the Zustand task store implementation.
 *
 * Features:
 * - Delta sync via deltaQueue table
 * - Bulk operations for 70K+ tasks
 * - Crash recovery support
 */

import Dexie, { Table } from 'dexie';
import type { TaskSnapshot, LinkSnapshot, JsonPatch } from '../types';
import type { LocationTreeNode } from '../../api/schedulingApis';

// Delta change record for crash recovery
export interface DeltaQueueEntry {
  id?: number; // Auto-increment
  taskId: string;
  changes: Partial<TaskSnapshot>;
  timestamp: number;
  synced: boolean;
  sessionId: string;
}

// Metadata for store state
export interface StoreMetadata {
  key: string;
  value: any;
  updatedAt: number;
}

// Location tree cached per project (for hydrate/persist like tasks)
export interface LocationTreeEntry {
  projectId: string;
  tree: LocationTreeNode[];
  updatedAt: number;
}

/**
 * GanttDatabase - Dexie schema for task persistence
 */
export class GanttDatabase extends Dexie {
  tasks!: Table<TaskSnapshot, string>;
  links!: Table<LinkSnapshot, number>;
  deltaQueue!: Table<DeltaQueueEntry, number>;
  metadata!: Table<StoreMetadata, string>;
  locationTrees!: Table<LocationTreeEntry, string>;

  constructor() {
    super('GanttScheduler');

    this.version(1).stores({
      // Tasks indexed by id, with projectId for filtering
      tasks: 'id, projectId, parentTaskId',
      // Links indexed by id
      links: 'id, source, target',
      // Delta queue for crash recovery
      deltaQueue: '++id, taskId, synced, sessionId, timestamp',
      // Metadata for store state
      metadata: 'key',
    });

    this.version(2).stores({
      locationTrees: 'projectId',
    });
  }
}

// Singleton database instance
export const db = new GanttDatabase();

// ============================================
// Task Operations
// ============================================

/**
 * Load all tasks for a project from IndexedDB
 */
export async function loadTasksFromDB(projectId: string): Promise<{
  tasks: TaskSnapshot[];
  links: LinkSnapshot[];
} | null> {
  try {
    const startTime = performance.now();

    const [tasks, links] = await Promise.all([
      db.tasks.where('projectId').equals(projectId).toArray(),
      db.links.toArray(),
    ]);

    const duration = performance.now() - startTime;
    console.log(`[DB] Loaded ${tasks.length} tasks, ${links.length} links in ${duration.toFixed(2)}ms`);

    if (tasks.length === 0) {
      return null;
    }

    return { tasks, links };
  } catch (error) {
    console.error('[DB] Failed to load tasks:', error);
    return null;
  }
}

/**
 * Save all tasks for a project to IndexedDB (bulk operation)
 */
export async function saveTasksToDB(
  projectId: string,
  tasks: TaskSnapshot[],
  links: LinkSnapshot[]
): Promise<void> {
  try {
    const startTime = performance.now();

    await db.transaction('rw', [db.tasks, db.links], async () => {
      // Clear existing tasks for this project
      await db.tasks.where('projectId').equals(projectId).delete();
      // Bulk insert new tasks
      await db.tasks.bulkPut(tasks);
      // Clear and replace links
      await db.links.clear();
      await db.links.bulkPut(links);
    });

    const duration = performance.now() - startTime;
    console.log(`[DB] Saved ${tasks.length} tasks, ${links.length} links in ${duration.toFixed(2)}ms`);
  } catch (error) {
    console.error('[DB] Failed to save tasks:', error);
    throw error;
  }
}

/** Upsert task rows without clearing the project (local edit persist). */
export async function putTasksInDB(tasks: TaskSnapshot[]): Promise<void> {
  if (tasks.length === 0) return;
  const startTime = performance.now();
  await db.tasks.bulkPut(tasks);
  const duration = performance.now() - startTime;
  if (duration >= 50 || tasks.length > 20) {
    console.log(`[DB] Upserted ${tasks.length} tasks in ${duration.toFixed(2)}ms`);
  }
}

/**
 * Update a single task (delta update)
 */
export async function updateTaskInDB(
  taskId: string,
  changes: Partial<TaskSnapshot>
): Promise<void> {
  try {
    await db.tasks.update(taskId, changes);
  } catch (error) {
    console.error(`[DB] Failed to update task ${taskId}:`, error);
    throw error;
  }
}

/**
 * Batch update multiple tasks (for drag operations)
 */
export async function batchUpdateTasksInDB(
  updates: Map<string, Partial<TaskSnapshot>>
): Promise<void> {
  try {
    const startTime = performance.now();

    await db.transaction('rw', db.tasks, async () => {
      for (const [taskId, changes] of updates) {
        await db.tasks.update(taskId, changes);
      }
    });

    const duration = performance.now() - startTime;
    console.log(`[DB] Batch updated ${updates.size} tasks in ${duration.toFixed(2)}ms`);
  } catch (error) {
    console.error('[DB] Failed to batch update tasks:', error);
    throw error;
  }
}

// ============================================
// Delta Queue Operations (Crash Recovery)
// ============================================

/**
 * Queue a delta change (for crash recovery)
 */
export async function queueDelta(
  taskId: string,
  changes: Partial<TaskSnapshot>,
  sessionId: string
): Promise<void> {
  try {
    await db.deltaQueue.add({
      taskId,
      changes,
      timestamp: Date.now(),
      synced: false,
      sessionId,
    });
  } catch (error) {
    console.error('[DB] Failed to queue delta:', error);
    throw error;
  }
}

/**
 * Queue multiple deltas in a transaction
 */
export async function queueDeltas(
  deltas: Array<{ taskId: string; changes: Partial<TaskSnapshot> }>,
  sessionId: string
): Promise<void> {
  try {
    const startTime = performance.now();

    const entries = deltas.map((delta) => ({
      taskId: delta.taskId,
      changes: delta.changes,
      timestamp: Date.now(),
      synced: false,
      sessionId,
    }));

    await db.deltaQueue.bulkAdd(entries);

    const duration = performance.now() - startTime;
    console.log(`[DB] Queued ${deltas.length} deltas in ${duration.toFixed(2)}ms`);
  } catch (error) {
    console.error('[DB] Failed to queue deltas:', error);
    throw error;
  }
}

/**
 * Get pending (unsynced) deltas
 */
export async function getPendingDeltas(): Promise<DeltaQueueEntry[]> {
  try {
    return await db.deltaQueue.where('synced').equals(0).toArray();
  } catch (error) {
    console.error('[DB] Failed to get pending deltas:', error);
    return [];
  }
}

/**
 * Mark deltas as synced
 */
export async function markDeltasSynced(ids: number[]): Promise<void> {
  try {
    await db.deltaQueue.bulkUpdate(
      ids.map((id) => ({ key: id, changes: { synced: true } }))
    );
  } catch (error) {
    console.error('[DB] Failed to mark deltas synced:', error);
    throw error;
  }
}

/**
 * Clear synced deltas (cleanup)
 */
export async function clearSyncedDeltas(): Promise<void> {
  try {
    await db.deltaQueue.where('synced').equals(1).delete();
    console.log('[DB] Cleared synced deltas');
  } catch (error) {
    console.error('[DB] Failed to clear synced deltas:', error);
  }
}

/**
 * Clear all deltas for a session
 */
export async function clearDeltasForSession(sessionId: string): Promise<void> {
  try {
    await db.deltaQueue.where('sessionId').equals(sessionId).delete();
    console.log(`[DB] Cleared deltas for session ${sessionId}`);
  } catch (error) {
    console.error('[DB] Failed to clear deltas for session:', error);
  }
}

/**
 * Recover pending deltas from a previous session (crash recovery)
 */
export async function recoverPendingDeltas(): Promise<{
  hasRecovery: boolean;
  deltas: DeltaQueueEntry[];
}> {
  try {
    const pending = await getPendingDeltas();

    if (pending.length > 0) {
      console.log(`[DB] Found ${pending.length} pending deltas for recovery`);
      return { hasRecovery: true, deltas: pending };
    }

    return { hasRecovery: false, deltas: [] };
  } catch (error) {
    console.error('[DB] Failed to recover pending deltas:', error);
    return { hasRecovery: false, deltas: [] };
  }
}

// ============================================
// Metadata Operations
// ============================================

/**
 * Save metadata (e.g., last sync time, cache version)
 */
export async function saveMetadata(key: string, value: any): Promise<void> {
  try {
    await db.metadata.put({
      key,
      value,
      updatedAt: Date.now(),
    });
  } catch (error) {
    console.error(`[DB] Failed to save metadata ${key}:`, error);
  }
}

/**
 * Get metadata by key
 */
export async function getMetadata(key: string): Promise<any | null> {
  try {
    const entry = await db.metadata.get(key);
    return entry?.value ?? null;
  } catch (error) {
    console.error(`[DB] Failed to get metadata ${key}:`, error);
    return null;
  }
}

// ============================================
// Location Tree Operations
// ============================================

/**
 * Load location tree for a project from IndexedDB
 */
export async function loadLocationTreeFromDB(projectId: string): Promise<LocationTreeNode[] | null> {
  try {
    const entry = await db.locationTrees.get(projectId);
    if (!entry?.tree?.length) return null;
    console.log(`[DB] Loaded location tree for ${projectId} (${entry.tree.length} nodes)`);
    return entry.tree;
  } catch (error) {
    console.error('[DB] Failed to load location tree:', error);
    return null;
  }
}

/**
 * Save location tree for a project to IndexedDB
 */
export async function saveLocationTreeToDB(
  projectId: string,
  tree: LocationTreeNode[]
): Promise<void> {
  try {
    await db.locationTrees.put({
      projectId,
      tree,
      updatedAt: Date.now(),
    });
    console.log(`[DB] Saved location tree for ${projectId} (${tree.length} nodes)`);
  } catch (error) {
    console.error('[DB] Failed to save location tree:', error);
    throw error;
  }
}

/**
 * Clear location tree for a project from IndexedDB (e.g. after add/edit/delete so next fetch hits API).
 */
export async function clearLocationTreeForProject(projectId: string): Promise<void> {
  try {
    await db.locationTrees.delete(projectId);
    console.log(`[DB] Cleared location tree for ${projectId}`);
  } catch (error) {
    console.error('[DB] Failed to clear location tree for project:', error);
    throw error;
  }
}

// ============================================
// Database Management
// ============================================

/**
 * Clear all data for a project
 */
export async function clearProjectData(projectId: string): Promise<void> {
  try {
    await db.transaction('rw', [db.tasks, db.deltaQueue, db.locationTrees], async () => {
      await db.tasks.where('projectId').equals(projectId).delete();
      await db.deltaQueue.clear();
      await db.locationTrees.delete(projectId);
    });
    console.log(`[DB] Cleared data for project ${projectId}`);
  } catch (error) {
    console.error('[DB] Failed to clear project data:', error);
    throw error;
  }
}

/**
 * Clear entire database
 */
export async function clearDatabase(): Promise<void> {
  try {
    await db.transaction('rw', [db.tasks, db.links, db.deltaQueue, db.metadata, db.locationTrees], async () => {
      await db.tasks.clear();
      await db.links.clear();
      await db.deltaQueue.clear();
      await db.metadata.clear();
      await db.locationTrees.clear();
    });
    console.log('[DB] Database cleared');
  } catch (error) {
    console.error('[DB] Failed to clear database:', error);
    throw error;
  }
}

/**
 * Get database statistics
 */
export async function getDatabaseStats(): Promise<{
  taskCount: number;
  linkCount: number;
  pendingDeltaCount: number;
}> {
  try {
    const [taskCount, linkCount, pendingDeltaCount] = await Promise.all([
      db.tasks.count(),
      db.links.count(),
      db.deltaQueue.where('synced').equals(0).count(),
    ]);

    return { taskCount, linkCount, pendingDeltaCount };
  } catch (error) {
    console.error('[DB] Failed to get database stats:', error);
    return { taskCount: 0, linkCount: 0, pendingDeltaCount: 0 };
  }
}
